import { Injectable } from '@angular/core';
import {
  CannotAccessRecipeType,
  isCannotAccessRecipeType,
  RecipeData,
} from 'app/utilities/types/recipe';

@Injectable({
  providedIn: 'root',
})
export class RecipeState {
  private pages: Record<string, (RecipeData | CannotAccessRecipeType)[][]> = {};
  private pageCounts: Record<string, number> = {};
  private cachedRecipes: Record<string, RecipeData> = {};
  private favoriteRecipes: Record<string, boolean> = {};

  getPage(page: number, tab: string): (RecipeData | CannotAccessRecipeType)[] | undefined {
    try {
      return this.pages[tab][page - 1];
    } catch (error) {
      return undefined;
    }
  }

  setPage(page: number, recipes: (RecipeData | CannotAccessRecipeType)[], tab: string) {
    if (!this.pages[tab]) {
      this.pages[tab] = [];
    }
    this.pages[tab][page - 1] = recipes;
  }

  /**
   * @deprecated Use CookbookState `getTabRecipeCount`
   */
  getTabRecipeCount(tab: string): number | undefined {
    return this.pageCounts[tab];
  }

  /**
   * @deprecated Use CookbookState `setTabRecipeCount`
   * @param count Total number of recipes for the tab
   * @param tab The name of the tab
   */
  setTabRecipeCount(count: number, tab: string) {
    this.pageCounts[tab] = count;
  }

  getPreviousKey(page: number): { previousUUID: string; previousName: string } | undefined {
    try {
      if (page === 1) {
        return undefined;
      }
      if (!this.pages['mine'][page - 2]) {
        return undefined;
      }
      if (this.pages['mine'][page - 2].length === 0) {
        return undefined;
      }

      const previousRecipeData =
        this.pages['mine'][page - 2][this.pages['mine'][page - 2].length - 1];
      const previousUUID = isCannotAccessRecipeType(previousRecipeData)
        ? previousRecipeData.uuid
        : previousRecipeData.recipe.uuid;
      const previousName = isCannotAccessRecipeType(previousRecipeData)
        ? previousRecipeData.sortName
        : previousRecipeData.recipe.name;
      return {
        previousUUID,
        previousName,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  getTabsPreviousKey(page: number, tab: string): string | undefined {
    try {
      if (page === 1) {
        return undefined;
      }
      if (!this.pages[tab][page - 2]) {
        return undefined;
      }
      if (this.pages[tab][page - 2].length === 0) {
        return undefined;
      }

      const previousRecipeData = this.pages[tab][page - 2][this.pages[tab][page - 2].length - 1];
      const previousUUID = isCannotAccessRecipeType(previousRecipeData)
        ? previousRecipeData.uuid
        : previousRecipeData.recipe.uuid;
      const previousName = isCannotAccessRecipeType(previousRecipeData)
        ? previousRecipeData.sortName
        : previousRecipeData.recipe.name;
      return `${previousName.toLowerCase()}:${previousUUID}`;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  clearCachedPages() {
    this.pages = {};
    this.clearPageRecipeCounts();
  }

  clearPageRecipeCounts() {
    this.pageCounts = {};
  }

  clearCachedPage(tab: string) {
    delete this.pages[tab];
  }

  getRecipe(uuid: string): RecipeData {
    return this.cachedRecipes[uuid];
  }

  clearCachedRecipes() {
    this.cachedRecipes = {};
  }

  removeCachedRecipe(uuid: string) {
    delete this.cachedRecipes[uuid];
    this.removeCachedFavorite(uuid);
  }

  setCachedRecipe(recipeData: RecipeData) {
    this.cachedRecipes[recipeData.recipe.uuid] = recipeData;
  }

  setFavoriteRecipe(uuid: string, favorite: boolean) {
    this.favoriteRecipes[uuid] = favorite;
  }

  getFavoriteRecipe(uuid: string): boolean | undefined {
    try {
      return this.favoriteRecipes[uuid];
    } catch (error) {
      return undefined;
    }
  }

  removeCachedFavorite(uuid: string) {
    delete this.favoriteRecipes[uuid];
  }

  clearCachedFavorites() {
    this.favoriteRecipes = {};
  }

  clearCache() {
    this.clearCachedPages();
    this.clearCachedRecipes();
    this.clearCachedFavorites();
  }
}
